/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import { rtf2text } from '@mairistem/administres-rtf-parser';
import {
  detectFileType,
  loadPdfDocument,
  renderPdfPageFromProxy,
  resizeImageFromBase64,
  TiffDecoder,
} from '@mairistem/administres-utils';

export interface PreviewImage {
  src: string;
  name: string;
}

const TARGET_WIDTH = 1600;

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Vérifie la signature TIFF réelle (`II*\0` / `MM\0*`) plutôt que l'extension
 * du fichier : un fichier renommé en `.tif` (ex. JPEG) ferait sinon planter
 * silencieusement le décodage (aucun répertoire, donc aucune page).
 */
const isTiffFile = async (file: File): Promise<boolean> => {
  const header = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  const isLittleEndian =
    header[0] === 0x49 &&
    header[1] === 0x49 &&
    header[2] === 0x2a &&
    header[3] === 0x00;
  const isBigEndian =
    header[0] === 0x4d &&
    header[1] === 0x4d &&
    header[2] === 0x00 &&
    header[3] === 0x2a;
  return isLittleEndian || isBigEndian;
};

/**
 * Décode un TIFF (mono ou multipage) en une image par page, affichable par
 * `SectionCanva`.
 *
 * Le décodage lui-même vit dans `TiffDecoder` d'`administres-utils`, partagé
 * avec le module d'annotation ; ici on ne fait que renommer les pages au format
 * attendu par l'aperçu.
 */
const decodeTiff = async (file: File): Promise<PreviewImage[]> => {
  const buffer = await file.arrayBuffer();

  return TiffDecoder.fromArrayBuffer(buffer)
    .getPages()
    .map(({ src, pageNumber }) => ({ src, name: `Page ${pageNumber}` }));
};

/**
 * Convertit un fichier sélectionné en images affichables par `SectionCanva` :
 * TIFF décodé via `TiffDecoder`, PDF/image repris tel quel du pipeline de
 * `DocumentDisplay`.
 */
export const fileToPreviewImages = async (
  file: File,
): Promise<PreviewImage[]> => {
  if (await isTiffFile(file)) {
    return decodeTiff(file);
  }

  const base64 = await fileToBase64(file);
  const fileType = detectFileType(base64);

  if (fileType === 'image') {
    const thumbnailUrl = await resizeImageFromBase64(base64, TARGET_WIDTH);
    return [{ src: thumbnailUrl, name: file.name }];
  }

  if (fileType === 'pdf') {
    const pdf = await loadPdfDocument(base64);
    const pages: PreviewImage[] = [];
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      // eslint-disable-next-line no-await-in-loop
      const thumbnailUrl = await renderPdfPageFromProxy(
        pdf,
        pageNumber,
        TARGET_WIDTH,
      );
      pages.push({ src: thumbnailUrl, name: `Page ${pageNumber}` });
    }
    return pages;
  }

  return [];
};

/**
 * Réponse de `GET /documents/{id}/merge`, qui renvoie le binaire du document
 * (oui, `merge` : l'API inverse volontairement `merge` et `download`).
 */
export interface ReponseDocumentBinaire {
  document?: string;
  type?: string;
}

/**
 * Signatures des formats reconnus, lues sur les premiers octets du contenu.
 *
 * **La signature prime sur `dc_type`**, et ce n'est pas un excès de prudence :
 * en base, 22 381 documents déclarés `doc` sont en réalité des DOCX, 17 sont
 * des PDF et 2 des RTF — seuls 36 sont de vrais `.doc`. Se fier au type déclaré
 * revenait à ne pas afficher des documents parfaitement lisibles, voire à
 * envoyer une archive ZIP au décodeur TIFF.
 */
const SIGNATURES: Array<{ octets: string; format: FormatDocument }> = [
  // TIFF, petit et gros boutiste.
  { octets: 'II*\u0000', format: 'tiff' },
  { octets: 'MM\u0000*', format: 'tiff' },
  { octets: '%PDF', format: 'pdf' },
  // ZIP : DOCX et autres formats Office Open XML.
  { octets: 'PK\u0003\u0004', format: 'docx' },
  { octets: '{\\rtf', format: 'rtf' },
  // Word 97-2003 (OLE2/CFBF), qu'aucune bibliothèque front ne sait rendre.
  { octets: '\u00d0\u00cf\u0011\u00e0', format: 'doc' },
  { octets: '\u00ff\u00d8', format: 'image' }, // JPEG
  { octets: '\u0089PNG', format: 'image' },
  { octets: 'GIF8', format: 'image' },
];

/**
 * `dc_type` déclarés, utilisés en dernier recours.
 *
 * `txt` n'a par nature aucune signature : le type déclaré est le seul indice.
 */
const FORMATS_PAR_TYPE: Record<string, FormatDocument> = {
  tif: 'tiff',
  tiff: 'tiff',
  pdf: 'pdf',
  rtf: 'rtf',
  docx: 'docx',
  doc: 'doc',
  txt: 'txt',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
};

/** `atob` échoue sur un base64 tronqué : on préfère une chaîne vide. */
const safeAtob = (base64: string): string => {
  try {
    return atob(base64);
  } catch {
    return '';
  }
};

/** Format effectif du document, qui décide de la façon de l'afficher. */
export type FormatDocument =
  | 'tiff'
  | 'pdf'
  | 'image'
  | 'rtf'
  | 'txt'
  | 'docx'
  | 'doc'
  | 'inconnu';

export interface ApercuDocument {
  format: FormatDocument;
  pages: PreviewImage[];
  /**
   * Contenu brut, fourni pour les formats que l'appelant rend lui-même :
   * `docx-preview` a besoin des octets, et le téléchargement d'un `.doc`
   * aussi.
   */
  binaire?: Uint8Array;
}

/**
 * Reconnaît le format d'un document à partir de son contenu, puis de son type.
 *
 * Exporté pour être testable seul : c'est la brique dont dépend tout l'aperçu.
 */
export const reconnaitreFormat = (
  decode: string,
  type?: string,
): FormatDocument => {
  const signature = _.find(SIGNATURES, ({ octets }) =>
    _.startsWith(decode, octets),
  );

  if (signature) return signature.format;

  return FORMATS_PAR_TYPE[_.toLower(type ?? '')] ?? 'inconnu';
};

/** Convertit une chaîne binaire en octets. */
const versOctets = (binaire: string): Uint8Array => {
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) {
    octets[i] = binaire.charCodeAt(i);
  }
  return octets;
};

/**
 * Décode un texte brut, en UTF-8 puis en Windows-1252.
 *
 * `atob` rend une chaîne d'octets : les accents d'un fichier UTF-8 y
 * apparaîtraient en mojibake. Le repli sur Windows-1252 sert aux fichiers
 * produits par l'ancienne application, qui n'écrivait pas en UTF-8 ; il est
 * choisi sur la présence du caractère de remplacement, seul indice qu'un
 * décodage UTF-8 a échoué.
 */
const decoderTexte = (binaire: string): string => {
  const octets = versOctets(binaire);
  const utf8 = new TextDecoder('utf-8').decode(octets);

  if (!_.includes(utf8, '\ufffd')) return utf8;

  return new TextDecoder('windows-1252').decode(octets);
};

/**
 * Convertit le binaire d'un document renvoyé par l'API en aperçu affichable.
 *
 * Le format est reconnu **par la signature du contenu**, le `dc_type` ne
 * servant que de dernier recours : il est faux sur la quasi-totalité des
 * documents bureautiques de la base.
 *
 * Le format accompagne les pages parce qu'il commande la façon de les
 * afficher : `rtf` produit du texte, `docx` se rend depuis ses octets, et
 * `doc` ne s'affiche pas du tout.
 */
export const documentBinaireToApercu = async (
  reponse: ReponseDocumentBinaire,
  typeConnu?: string,
): Promise<ApercuDocument> => {
  const { document: base64, type: typeReponse } = reponse ?? {};

  if (!base64) return { format: 'inconnu', pages: [] };

  // Le binaire arrive parfois avec des retours à la ligne, que `atob` refuse.
  const propre = base64.replace(/\s/g, '');
  const decode = safeAtob(propre);
  const format = reconnaitreFormat(decode, typeReponse || typeConnu);

  if (format === 'tiff') {
    return {
      format,
      pages: TiffDecoder.fromBinaryString(decode)
        .getPages()
        .map(({ src, pageNumber }) => ({ src, name: `Page ${pageNumber}` })),
    };
  }

  if (format === 'rtf') {
    return { format, pages: [{ src: rtf2text(decode), name: 'Page 1' }] };
  }

  if (format === 'txt') {
    return { format, pages: [{ src: decoderTexte(decode), name: 'Page 1' }] };
  }

  if (format === 'pdf') {
    const pdf = await loadPdfDocument(propre);
    const pages: PreviewImage[] = [];
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const thumbnailUrl = await renderPdfPageFromProxy(
        pdf,
        pageNumber,
        TARGET_WIDTH,
      );
      pages.push({ src: thumbnailUrl, name: `Page ${pageNumber}` });
    }
    return { format, pages };
  }

  if (format === 'image') {
    return {
      format,
      pages: [
        {
          src: await resizeImageFromBase64(propre, TARGET_WIDTH),
          name: 'Page 1',
        },
      ],
    };
  }

  // `docx` est rendu par l'appelant depuis ses octets ; `doc` ne s'affiche pas,
  // mais son contenu sert au téléchargement.
  return { format, pages: [], binaire: versOctets(decode) };
};

export default fileToPreviewImages;
